/********************************************************************
 * ADOBE CONFIDENTIAL
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

const promiseTimeout = <T>(
	promise: Promise<T>,
	timeout: number,
	timeoutException?: unknown,
) => {
	let timerId: ReturnType<typeof setTimeout>;

	const timeoutPromise = new Promise((_, reject) => {
		timerId = setTimeout(reject, timeout, timeoutException);
	});

	return Promise.race([timeoutPromise, promise]).finally(() =>
		clearTimeout(timerId),
	);
};

const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

const waitForPromise = (timer: number, promise: Promise<any>) =>
	new Promise((resolve) => {
		const id = setTimeout(() => {
			resolve(true);
		}, timer);

		// TODO: Handle promise rejection properly (add error handling or await as needed). See https://git.corp.adobe.com/nest/nest/blob/main/docs/best-practices/no-floating-promises.md
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		(async () => {
			if (typeof promise === 'object' && typeof promise.then === 'function') {
				try {
					await promise;
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error(e);
				} finally {
					clearTimeout(id);
					resolve(true);
				}
			}
		})();
	});

const waitForPromiseWithValues = (timer: number, promise: Promise<any>) =>
	new Promise((resolve) => {
		const id = setTimeout(() => {
			// Resolve with the fallback value if the timer expires
			resolve({
				resolvedViaTimeout: true,
			});
		}, timer);

		// TODO: Handle promise rejection properly (add error handling or await as needed). See https://git.corp.adobe.com/nest/nest/blob/main/docs/best-practices/no-floating-promises.md
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		(async () => {
			if (typeof promise === 'object' && typeof promise.then === 'function') {
				try {
					const result = await promise;
					clearTimeout(id); // Clear the timeout if the promise resolves
					resolve(result); // Resolve with the promise's resolved value
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error(e);
				} finally {
					clearTimeout(id);
				}
			}
		})();
	});

const PromiseUtils = {
	promiseTimeout,
	wait,
	waitForPromise,
	waitForPromiseWithValues,
};

export {PromiseUtils};
